import { useRef, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform, AnimatePresence } from "framer-motion";
import {
  Phone, MessageCircle, Mail, ShoppingBag, Share2, Globe, Clock,
  MessageSquareHeart, Layers, UserCheck, ArrowRight, BadgeCheck,
  SlidersHorizontal, History, PhoneCall, Sparkles, ChevronRight, Brain,
  CreditCard, Truck, Star, CalendarCheck, BellRing, HeartHandshake, Users,
  Languages, GraduationCap, Code2, Play, Pause, Scissors,
} from "lucide-react";
import SiteHeader from "@/components/site/SiteHeader";
import SiteFooter from "@/components/site/SiteFooter";
import { Reveal, MaskedLines, Eyebrow } from "@/lib/motion";
import { useLang, HOME } from "@/lib/language";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";

const FEATURE_STYLE = [
  { icon: Phone, cls: "bg-orange-500" },
  { icon: MessageCircle, cls: "bg-emerald-500" },
  { icon: Mail, cls: "bg-sky-500" },
  { icon: ShoppingBag, cls: "bg-violet-500" },
  { icon: Share2, cls: "bg-rose-500" },
  { icon: Globe, cls: "bg-slate-700" },
];

const TRUST_STYLE = [
  { icon: Clock, cls: "bg-orange-50 text-orange-600" },
  { icon: MessageSquareHeart, cls: "bg-emerald-50 text-emerald-600" },
  { icon: Layers, cls: "bg-sky-50 text-sky-600" },
  { icon: UserCheck, cls: "bg-violet-50 text-violet-600" },
];

const UND_STYLE = [
  { icon: Brain, cls: "bg-violet-50 text-violet-600" },
  { icon: GraduationCap, cls: "bg-orange-50 text-orange-600" },
  { icon: Languages, cls: "bg-emerald-50 text-emerald-600" },
  { icon: Code2, cls: "bg-sky-50 text-sky-600" },
];

const AGENT_STYLE = [
  { icon: PhoneCall, cls: "bg-orange-500/15 text-orange-400 ring-orange-500/30" },
  { icon: MessageCircle, cls: "bg-emerald-500/15 text-emerald-400 ring-emerald-500/30" },
  { icon: Mail, cls: "bg-sky-500/15 text-sky-400 ring-sky-500/30" },
  { icon: ShoppingBag, cls: "bg-violet-500/15 text-violet-400 ring-violet-500/30" },
  { icon: Share2, cls: "bg-rose-500/15 text-rose-400 ring-rose-500/30" },
];

const AGENT_AVATAR = ["bg-orange-500", "bg-emerald-500", "bg-sky-500", "bg-violet-500", "bg-rose-500"];

const TEAM_STAT_ICONS = [Users, Sparkles, Code2];

const CTRL_STYLE = [
  { icon: BadgeCheck, cls: "bg-emerald-50 text-emerald-600" },
  { icon: UserCheck, cls: "bg-orange-50 text-orange-600" },
  { icon: SlidersHorizontal, cls: "bg-sky-50 text-sky-600" },
  { icon: History, cls: "bg-violet-50 text-violet-600" },
];

const HOW_NUM = ["text-orange-200", "text-emerald-200", "text-sky-200"];

const FLOW_STYLE = {
  order: [
    { icon: PhoneCall, color: "bg-orange-100 text-orange-600 ring-orange-200" },
    { icon: Brain, color: "bg-violet-100 text-violet-600 ring-violet-200" },
    { icon: ShoppingBag, color: "bg-emerald-100 text-emerald-600 ring-emerald-200" },
    { icon: CreditCard, color: "bg-sky-100 text-sky-600 ring-sky-200" },
    { icon: Truck, color: "bg-amber-100 text-amber-600 ring-amber-200" },
    { icon: Star, color: "bg-rose-100 text-rose-600 ring-rose-200" },
  ],
  appointment: [
    { icon: PhoneCall, color: "bg-orange-100 text-orange-600 ring-orange-200" },
    { icon: Brain, color: "bg-violet-100 text-violet-600 ring-violet-200" },
    { icon: CalendarCheck, color: "bg-emerald-100 text-emerald-600 ring-emerald-200" },
    { icon: BellRing, color: "bg-sky-100 text-sky-600 ring-sky-200" },
    { icon: HeartHandshake, color: "bg-rose-100 text-rose-600 ring-rose-200" },
  ],
  salon: [
    { icon: PhoneCall, color: "bg-orange-100 text-orange-600 ring-orange-200" },
    { icon: Brain, color: "bg-violet-100 text-violet-600 ring-violet-200" },
    { icon: Scissors, color: "bg-emerald-100 text-emerald-600 ring-emerald-200" },
    { icon: BellRing, color: "bg-sky-100 text-sky-600 ring-sky-200" },
    { icon: HeartHandshake, color: "bg-rose-100 text-rose-600 ring-rose-200" },
  ],
};

const FLOW_TAB_ICONS = { order: ShoppingBag, appointment: CalendarCheck, salon: Scissors };

function DashboardPreview() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [30, -30]);

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 60, scale: 0.96 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ duration: 1, delay: 0.7, ease: [0.22, 1, 0.36, 1] }}
      className="relative mx-auto mt-14 max-w-4xl"
      data-testid="hero-dashboard-preview"
    >
      <motion.div style={{ y }} className="relative">
        <div className="absolute -inset-6 rounded-[2rem] bg-orange-200/50 blur-3xl" aria-hidden />
        <div className="relative overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl shadow-orange-900/10">
          <div className="flex items-center gap-2 border-b border-slate-100 bg-slate-50/80 px-4 py-3">
            <span className="h-3 w-3 rounded-full bg-rose-400" />
            <span className="h-3 w-3 rounded-full bg-amber-400" />
            <span className="h-3 w-3 rounded-full bg-emerald-400" />
            <span className="ml-3 rounded-md bg-white px-3 py-1 text-xs text-slate-500 ring-1 ring-slate-200">
              app.creativesolution.ai — Sharma Electronics
            </span>
            <span className="ml-auto inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-2.5 py-1 text-[11px] font-semibold text-emerald-700 ring-1 ring-emerald-200">
              <span className="h-1.5 w-1.5 animate-soft-pulse rounded-full bg-emerald-500" /> AI Active
            </span>
          </div>
          <div className="grid gap-4 p-4 sm:grid-cols-3 sm:p-6">
            {[
              { label: "Calls answered today", value: "23", icon: PhoneCall, tone: "text-orange-600 bg-orange-50" },
              { label: "WhatsApp replies", value: "41", icon: MessageCircle, tone: "text-emerald-600 bg-emerald-50" },
              { label: "Orders received", value: "12", icon: ShoppingBag, tone: "text-violet-600 bg-violet-50" },
            ].map((s) => (
              <div key={s.label} className="rounded-xl border border-slate-100 bg-white p-4">
                <div className={`inline-flex rounded-lg p-2 ${s.tone}`}>
                  <s.icon className="h-4 w-4" />
                </div>
                <p className="mt-3 font-display text-2xl font-bold text-slate-900">{s.value}</p>
                <p className="text-xs text-slate-500">{s.label}</p>
              </div>
            ))}
            <div className="rounded-xl border border-slate-100 bg-orange-50/40 p-4 sm:col-span-3">
              <div className="flex items-start gap-3">
                <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-orange-600 text-white">
                  <Sparkles className="h-4 w-4" />
                </div>
                <div className="min-w-0">
                  <p className="text-sm text-slate-700">
                    <span className="font-semibold text-slate-900">AI replied to Ramesh on WhatsApp:</span>{" "}
                    "Namaste! The Samsung 1.5-ton AC is available for ₹38,500 with free installation. Shall I book a delivery for Saturday?"
                  </p>
                  <p className="mt-1.5 text-xs text-slate-400">Just now · Order created · Customer confirmed</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}

function WorkflowFlow({ steps, activeIdx, testidPrefix }) {
  return (
    <div className="relative" data-testid={`${testidPrefix}-flow`}>
      <div className="absolute bottom-6 left-[27px] top-6 border-l-2 border-dashed border-orange-300" aria-hidden />
      <div className="space-y-5">
        {steps.map((s, i) => {
          const isActive = i === activeIdx;
          const isDone = activeIdx > i;
          return (
            <Reveal key={s.t} delay={i * 0.05}>
              <div className="relative flex gap-5" data-testid={`${testidPrefix}-step-${i + 1}`}>
                <div
                  className={`relative z-10 flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl ring-4 ring-offset-2 ring-offset-white transition-all duration-300 ${s.color} ${
                    isActive ? "scale-110 shadow-lg" : ""
                  }`}
                >
                  <s.icon className="h-6 w-6" />
                  {isActive && (
                    <span className="absolute -right-1 -top-1 flex h-4 w-4">
                      <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-orange-400 opacity-75" />
                      <span className="relative inline-flex h-4 w-4 rounded-full bg-orange-500" />
                    </span>
                  )}
                </div>
                <div
                  className={`flex-1 rounded-2xl border bg-white p-5 transition-all duration-300 ${
                    isActive
                      ? "border-orange-400 shadow-xl shadow-orange-600/10"
                      : isDone
                      ? "border-emerald-200 shadow-sm"
                      : "border-slate-100 shadow-sm"
                  }`}
                >
                  <div className="flex items-center gap-2.5">
                    <span className={`font-display text-xs font-bold ${isDone ? "text-emerald-500" : "text-orange-500"}`}>
                      STEP {i + 1}
                    </span>
                    <h4 className="font-display text-base font-bold text-slate-900">{s.t}</h4>
                  </div>
                  <p className="mt-1.5 text-sm leading-relaxed text-slate-600">{s.d}</p>
                </div>
              </div>
            </Reveal>
          );
        })}
      </div>
    </div>
  );
}

export default function Home() {
  const [demoOpen, setDemoOpen] = useState(false);
  const [flowTab, setFlowTab] = useState("order");
  const [playing, setPlaying] = useState(false);
  const [activeStep, setActiveStep] = useState(-1);
  const { lang } = useLang();
  const s = HOME[lang];

  const stepCount = s.flows[flowTab].steps.length;

  useEffect(() => {
    if (!playing) return;
    const id = setInterval(() => {
      setActiveStep((p) => (p + 1 >= stepCount ? 0 : p + 1));
    }, 2100);
    return () => clearInterval(id);
  }, [playing, stepCount]);

  const flowSteps = s.flows[flowTab].steps.map((st, i) => ({ ...st, ...FLOW_STYLE[flowTab][i] }));

  const heroLines = s.heroLines.map((line, i) => (
    <span key={i} className={i === 1 ? "text-orange-600" : ""}>{line}</span>
  ));

  return (
    <div className="bg-white" data-testid="homepage">
      <SiteHeader />

      {/* HERO */}
      <section className="relative overflow-hidden" data-testid="hero-section">
        <div className="pointer-events-none absolute inset-0" aria-hidden>
          <div className="absolute -top-24 right-[-10%] h-[420px] w-[420px] rounded-full bg-orange-200/50 blur-3xl" />
          <div className="absolute bottom-0 left-[-10%] h-[320px] w-[320px] rounded-full bg-emerald-100/60 blur-3xl" />
        </div>
        <div className="relative mx-auto max-w-7xl px-4 pb-16 pt-16 text-center sm:px-6 md:pt-24 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="inline-flex items-center gap-2 rounded-full border border-orange-200 bg-orange-50 px-4 py-1.5 text-xs font-semibold text-orange-800"
            data-testid="hero-talkid-badge"
          >
            <Phone className="h-3.5 w-3.5" /> {s.heroBadge}
          </motion.div>
          <MaskedLines
            className="mt-6 font-display text-4xl font-bold leading-[1.12] tracking-tight text-slate-900 sm:text-5xl lg:text-6xl"
            lines={heroLines}
          />
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.55 }}
            className="mx-auto mt-6 max-w-2xl text-base leading-relaxed text-slate-600 sm:text-lg"
            data-testid="hero-description"
          >
            {s.heroDesc}
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.7 }}
            className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row"
          >
            <Link
              to="/onboarding"
              data-testid="hero-start-ai-employee-button"
              className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-orange-600 px-7 py-3.5 text-base font-semibold text-white shadow-md shadow-orange-600/25 transition-all hover:bg-orange-700 hover:shadow-lg sm:w-auto"
            >
              {s.ctaStart} <ArrowRight className="h-4 w-4" />
            </Link>
            <a
              href="#how-it-works"
              data-testid="hero-see-how-it-works-button"
              className="inline-flex w-full items-center justify-center gap-2 rounded-xl border-2 border-slate-200 bg-white px-7 py-3.5 text-base font-semibold text-slate-800 transition-all hover:border-orange-300 hover:bg-orange-50 sm:w-auto"
            >
              {s.ctaHow}
            </a>
          </motion.div>
          <DashboardPreview />
        </div>
      </section>

      {/* MARQUEE */}
      <div className="overflow-hidden bg-orange-600 py-4" data-testid="capability-marquee">
        <div className="animate-marquee flex w-max items-center gap-10">
          {[...s.marquee, ...s.marquee].map((item, i) => (
            <span key={i} className="flex items-center gap-10 whitespace-nowrap text-sm font-semibold text-white">
              {item} <span className="h-1.5 w-1.5 rounded-full bg-white/60" />
            </span>
          ))}
        </div>
      </div>

      {/* TRUST STRIP */}
      <section className="mx-auto max-w-7xl px-4 py-14 sm:px-6 lg:px-8" data-testid="trust-strip">
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
          {s.trust.map((t, i) => {
            const st = TRUST_STYLE[i];
            return (
              <Reveal key={t.t} delay={i * 0.08}>
                <div className="h-full rounded-2xl border border-slate-100 bg-white p-5 transition-all hover:-translate-y-1 hover:shadow-lg" data-testid={`trust-item-${i}`}>
                  <div className={`inline-flex rounded-xl p-2.5 ${st.cls}`}>
                    <st.icon className="h-5 w-5" />
                  </div>
                  <p className="mt-3 font-display text-base font-bold text-slate-900">{t.t}</p>
                  <p className="mt-1 text-sm text-slate-500">{t.d}</p>
                </div>
              </Reveal>
            );
          })}
        </div>
      </section>

      {/* FEATURES */}
      <section id="features" className="bg-orange-50/40 py-16 md:py-24" data-testid="features-section">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <Reveal className="max-w-2xl">
            <Eyebrow>{s.featEyebrow}</Eyebrow>
            <h2 className="mt-4 font-display text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl lg:text-4xl">
              {s.featTitle}
            </h2>
            <p className="mt-4 text-base text-slate-600 sm:text-lg">{s.featDesc}</p>
          </Reveal>
          <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {s.features.map((f, i) => {
              const st = FEATURE_STYLE[i];
              return (
                <Reveal key={f.t} delay={(i % 3) * 0.08}>
                  <div className="group h-full rounded-2xl border border-slate-100 bg-white p-6 transition-all hover:-translate-y-1 hover:shadow-xl md:p-8" data-testid={`feature-card-${i}`}>
                    <div className={`inline-flex rounded-xl p-3 text-white shadow-md transition-transform group-hover:scale-110 ${st.cls}`}>
                      <st.icon className="h-6 w-6" />
                    </div>
                    <h3 className="mt-5 font-display text-lg font-bold text-slate-900 sm:text-xl">{f.t}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-slate-600">{f.d}</p>
                    {f.badge && (
                      <span className="mt-4 inline-block rounded-full bg-orange-100 px-3 py-1 text-xs font-semibold text-orange-800">
                        {f.badge}
                      </span>
                    )}
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* UNDERSTANDS, NOT A ROBOT */}
      <section className="py-16 md:py-24" data-testid="understanding-section">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid items-center gap-10 lg:grid-cols-2 lg:gap-16">
            <Reveal>
              <Eyebrow>{s.undEyebrow}</Eyebrow>
              <h2 className="mt-4 font-display text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl lg:text-4xl">
                {s.undTitle}
              </h2>
              <p className="mt-4 text-base leading-relaxed text-slate-600 sm:text-lg">{s.undDesc}</p>
              <ul className="mt-8 space-y-5">
                {s.undBullets.map((c, i) => {
                  const st = UND_STYLE[i];
                  return (
                    <li key={c.t} className="flex gap-4">
                      <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${st.cls}`}>
                        <st.icon className="h-5 w-5" />
                      </div>
                      <div>
                        <p className="font-display text-base font-bold text-slate-900">{c.t}</p>
                        <p className="text-sm leading-relaxed text-slate-600">{c.d}</p>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </Reveal>
            <Reveal delay={0.15}>
              <div className="rounded-3xl border border-slate-100 bg-slate-50/70 p-5 shadow-inner sm:p-7" data-testid="understanding-chat-demo">
                <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">{s.chatLabel}</p>
                <div className="mt-4 space-y-3.5">
                  <div className="flex justify-start">
                    <div className="max-w-[85%] rounded-2xl rounded-tl-sm bg-white px-4 py-3 text-sm text-slate-800 ring-1 ring-slate-200">
                      Bhaiya, woh blue wala kurta jo kal poocha tha — ab aaya kya? Aur kal shaam tak mil jayega?
                      <span className="mt-1 block text-[11px] text-slate-400">Customer, WhatsApp · 2:14 PM</span>
                    </div>
                  </div>
                  <div className="flex justify-end">
                    <div className="max-w-[85%] rounded-2xl rounded-tr-sm bg-orange-600 px-4 py-3 text-sm text-white">
                      <span className="mb-1 inline-flex items-center gap-1 rounded-full bg-white/20 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide">
                        <Sparkles className="h-2.5 w-2.5" /> AI Employee
                      </span>
                      <p>Namaste ji! Haan, blue kurta (size M, L, XL) abhi stock mein hai — ₹1,299. Aaj 5 baje tak order karein toh kal shaam tak delivery ho jayegi. Order kar doon?</p>
                      <span className="mt-1 block text-right text-[11px] text-orange-100">Understood yesterday's chat + checked live stock + delivery promise</span>
                    </div>
                  </div>
                  <div className="flex justify-start">
                    <div className="max-w-[85%] rounded-2xl rounded-tl-sm bg-white px-4 py-3 text-sm text-slate-800 ring-1 ring-slate-200">
                      Haan L size bhej do, pata same hai
                      <span className="mt-1 block text-[11px] text-slate-400">Customer, WhatsApp · 2:16 PM</span>
                    </div>
                  </div>
                  <div className="flex justify-end">
                    <div className="max-w-[85%] rounded-2xl rounded-tr-sm bg-orange-600 px-4 py-3 text-sm text-white">
                      <span className="mb-1 inline-flex items-center gap-1 rounded-full bg-white/20 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide">
                        <Sparkles className="h-2.5 w-2.5" /> AI Employee
                      </span>
                      <p>Done! Blue kurta, size L — aapke saved address par. Payment link bhej raha hoon, aur order details email par bhi mil jayengi. Dhanyavaad!</p>
                      <span className="mt-1 block text-right text-[11px] text-orange-100">Order placed · Payment link sent · Email confirmation sent</span>
                    </div>
                  </div>
                </div>
                <p className="mt-4 rounded-xl bg-white px-4 py-3 text-center text-xs font-medium text-slate-500 ring-1 ring-slate-100">
                  {s.chatNote}
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* AI TEAM */}
      <section className="relative overflow-hidden bg-slate-900 py-16 md:py-24" data-testid="ai-team-section">
        <div className="pointer-events-none absolute inset-0" aria-hidden>
          <div className="absolute -top-32 left-1/3 h-96 w-96 rounded-full bg-orange-500/15 blur-3xl" />
          <div className="absolute -bottom-32 right-1/4 h-80 w-80 rounded-full bg-emerald-500/10 blur-3xl" />
        </div>
        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <Reveal className="mx-auto max-w-3xl text-center">
            <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-orange-400">
              <span className="h-px w-6 bg-orange-400" /> {s.teamEyebrow}
            </span>
            <h2 className="mt-4 font-display text-2xl font-bold tracking-tight text-white sm:text-3xl lg:text-4xl">
              {s.teamTitle}
            </h2>
            <p className="mx-auto mt-4 max-w-2xl text-base leading-relaxed text-slate-300">{s.teamDesc}</p>
          </Reveal>

          <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-5">
            {s.agents.map((a, i) => {
              const st = AGENT_STYLE[i];
              return (
                <Reveal key={a.role} delay={i * 0.08}>
                  <div className="h-full rounded-2xl border border-white/10 bg-white/5 p-6 text-center transition-colors hover:bg-white/10" data-testid={`ai-agent-${i}`}>
                    <div className="relative mx-auto w-fit">
                      <div className={`inline-flex rounded-2xl p-3.5 ring-1 ${st.cls}`}>
                        <st.icon className="h-6 w-6" />
                      </div>
                      <span className={`absolute -bottom-2 -right-2 flex h-7 w-7 items-center justify-center rounded-full font-display text-xs font-bold text-white ring-2 ring-slate-900 ${AGENT_AVATAR[i]}`}>
                        {a.name[0]}
                      </span>
                    </div>
                    <p className="mt-5 font-display text-base font-bold text-white">{a.name}</p>
                    <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">{a.role}</p>
                    <p className="mt-1.5 text-sm leading-relaxed text-slate-400">{a.d}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>

          <Reveal delay={0.15}>
            <div className="mx-auto mt-10 flex max-w-3xl flex-col items-center justify-center gap-4 rounded-2xl border border-orange-500/30 bg-orange-500/10 p-6 text-center sm:flex-row sm:gap-8" data-testid="ai-team-stats">
              {s.teamStats.map((st, i) => {
                const Icon = TEAM_STAT_ICONS[i];
                return (
                  <div key={st.big} className="flex items-center gap-3">
                    {i > 0 && <span className="hidden h-10 w-px bg-white/10 sm:block" />}
                    <Icon className="h-8 w-8 text-orange-400" />
                    <p className="text-left text-sm text-slate-300">
                      <span className="font-display text-xl font-bold text-white">{st.big}</span>
                      <br />
                      {st.small}
                    </p>
                  </div>
                );
              })}
            </div>
          </Reveal>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section id="how-it-works" className="py-16 md:py-24" data-testid="how-it-works-section">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <Reveal className="mx-auto max-w-2xl text-center">
            <Eyebrow>{s.howEyebrow}</Eyebrow>
            <h2 className="mt-4 font-display text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl lg:text-4xl">
              {s.howTitle}
            </h2>
          </Reveal>
          <div className="mt-14 grid gap-8 md:grid-cols-3">
            {s.howSteps.map((step, i) => (
              <Reveal key={step.t} delay={i * 0.12}>
                <div className="relative h-full rounded-2xl border border-slate-100 bg-white p-8 shadow-sm transition-shadow hover:shadow-lg" data-testid={`how-step-${i + 1}`}>
                  <span className={`font-display text-6xl font-bold ${HOW_NUM[i]}`}>0{i + 1}</span>
                  <h3 className="mt-4 font-display text-lg font-bold text-slate-900 sm:text-xl">{step.t}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-slate-600">{step.d}</p>
                  {i < 2 && (
                    <ChevronRight className="absolute -right-4 top-1/2 hidden h-6 w-6 -translate-y-1/2 text-orange-400 md:block" aria-hidden />
                  )}
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* REAL WORKFLOWS */}
      <section className="bg-orange-50/40 py-16 md:py-24" data-testid="workflows-section">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <Reveal className="mx-auto max-w-2xl text-center">
            <Eyebrow>{s.wfEyebrow}</Eyebrow>
            <h2 className="mt-4 font-display text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl lg:text-4xl">
              {s.wfTitle}
            </h2>
            <p className="mt-4 text-base text-slate-600">{s.wfDesc}</p>
          </Reveal>

          <Reveal delay={0.1} className="mt-10">
            <div className="flex flex-col items-center justify-center gap-4 sm:flex-row">
              <div className="flex w-fit rounded-2xl bg-white p-1.5 shadow-sm ring-1 ring-slate-200" data-testid="workflow-tabs">
                {Object.keys(s.flows).map((key) => {
                  const Icon = FLOW_TAB_ICONS[key];
                  return (
                    <button
                      key={key}
                      data-testid={`workflow-tab-${key}`}
                      onClick={() => { setFlowTab(key); setActiveStep(-1); }}
                      className={`inline-flex items-center gap-2 rounded-xl px-3.5 py-2.5 text-xs font-semibold transition-all sm:px-5 sm:text-sm ${
                        flowTab === key ? "bg-orange-600 text-white shadow-md shadow-orange-600/25" : "text-slate-500 hover:text-slate-800"
                      }`}
                    >
                      <Icon className="h-4 w-4" /> {s.flows[key].label}
                    </button>
                  );
                })}
              </div>
              <button
                data-testid="workflow-play-demo-button"
                onClick={() => { setPlaying(!playing); if (!playing && activeStep >= stepCount - 1) setActiveStep(-1); }}
                className={`inline-flex items-center gap-2 rounded-xl px-5 py-2.5 text-sm font-semibold transition-all ${
                  playing
                    ? "bg-slate-900 text-white hover:bg-slate-800"
                    : "border-2 border-orange-500 bg-white text-orange-700 hover:bg-orange-50"
                }`}
              >
                {playing ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
                {playing ? s.wfPause : s.wfPlay}
              </button>
            </div>
            {playing && (
              <p className="mt-3 text-center text-xs font-semibold text-orange-600" data-testid="demo-step-indicator">
                Step {Math.min(activeStep + 1, stepCount)} / {stepCount}
              </p>
            )}
          </Reveal>

          <div className="mt-10">
            <AnimatePresence mode="wait">
              <motion.div
                key={flowTab + lang}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -16 }}
                transition={{ duration: 0.35 }}
              >
                <WorkflowFlow steps={flowSteps} activeIdx={playing ? activeStep : -1} testidPrefix={`${flowTab}-workflow`} />
              </motion.div>
            </AnimatePresence>
          </div>

          <Reveal className="mt-10 text-center">
            <p className="mx-auto max-w-xl rounded-2xl border border-orange-200 bg-white px-6 py-4 text-sm font-medium text-slate-600" data-testid="workflow-note">
              {s.wfNote}
            </p>
          </Reveal>
        </div>
      </section>

      {/* YOU STAY IN CONTROL */}
      <section className="py-16 md:py-24" data-testid="control-section">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid items-center gap-10 lg:grid-cols-2 lg:gap-16">
            <Reveal>
              <Eyebrow>{s.ctrlEyebrow}</Eyebrow>
              <h2 className="mt-4 font-display text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl lg:text-4xl">
                {s.ctrlTitle}
              </h2>
              <p className="mt-4 text-base text-slate-600 sm:text-lg">{s.ctrlDesc}</p>
              <ul className="mt-8 space-y-5">
                {s.ctrlItems.map((c, i) => {
                  const st = CTRL_STYLE[i];
                  return (
                    <li key={c.t} className="flex gap-4" data-testid={`control-item-${i}`}>
                      <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${st.cls}`}>
                        <st.icon className="h-5 w-5" />
                      </div>
                      <div>
                        <p className="font-display text-base font-bold text-slate-900">{c.t}</p>
                        <p className="text-sm text-slate-600">{c.d}</p>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </Reveal>
            <Reveal delay={0.15}>
              <div className="rounded-2xl border border-orange-200 bg-orange-50/50 p-6 md:p-8" data-testid="control-approval-example">
                <p className="text-xs font-semibold uppercase tracking-wider text-orange-700">{s.apprLabel}</p>
                <div className="mt-4 rounded-xl bg-white p-5 shadow-sm ring-1 ring-slate-100">
                  <div className="flex items-center justify-between">
                    <p className="font-display text-base font-bold text-slate-900">{s.apprTitle}</p>
                    <span className="rounded-full bg-orange-100 px-2.5 py-1 text-xs font-semibold text-orange-800">{s.apprWaiting}</span>
                  </div>
                  <p className="mt-2 text-sm text-slate-600">{s.apprDesc}</p>
                  <div className="mt-5 flex gap-3">
                    <button
                      data-testid="control-approve-button"
                      onClick={() => toast.success("Design preview: refund approved")}
                      className="flex-1 rounded-xl bg-emerald-600 px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-emerald-700"
                    >
                      {s.apprApprove}
                    </button>
                    <button
                      data-testid="control-review-button"
                      onClick={() => toast.info("Design preview: conversation opens for review")}
                      className="flex-1 rounded-xl border-2 border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 transition-colors hover:bg-slate-50"
                    >
                      {s.apprReview}
                    </button>
                  </div>
                </div>
                <p className="mt-4 text-xs text-slate-500">{s.apprNote}</p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* PRICING PREVIEW */}
      <section className="bg-orange-50/40 py-16 md:py-24" data-testid="pricing-preview-section">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <Reveal className="mx-auto max-w-2xl text-center">
            <Eyebrow>{s.priceEyebrow}</Eyebrow>
            <h2 className="mt-4 font-display text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl lg:text-4xl">
              {s.priceTitle}
            </h2>
            <p className="mt-4 text-base text-slate-600">{s.priceDesc}</p>
          </Reveal>
          <div className="mt-12 grid gap-6 md:grid-cols-3">
            {s.plans.map((p, i) => (
              <Reveal key={p.name} delay={i * 0.1}>
                <div
                  className={`relative h-full rounded-2xl bg-white p-7 ${i === 1 ? "border-2 border-orange-500 shadow-xl shadow-orange-600/10" : "border border-slate-200"}`}
                  data-testid={`pricing-preview-${p.name.toLowerCase()}`}
                >
                  {i === 1 && (
                    <span className="absolute -top-3.5 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-full bg-orange-600 px-4 py-1 text-xs font-bold text-white">
                      {s.popular}
                    </span>
                  )}
                  <h3 className="font-display text-lg font-bold text-slate-900">{p.name}</h3>
                  <p className="mt-1 text-sm text-slate-500">{p.d}</p>
                  <p className="mt-5 font-display text-3xl font-bold text-slate-900">
                    ₹X,XXX<span className="text-base font-medium text-slate-500">/month</span>
                  </p>
                  <Link
                    to="/pricing"
                    data-testid={`pricing-preview-cta-${p.name.toLowerCase()}`}
                    className={`mt-6 block rounded-xl px-5 py-3 text-center text-sm font-semibold transition-colors ${
                      i === 1
                        ? "bg-orange-600 text-white hover:bg-orange-700"
                        : "border-2 border-slate-200 text-slate-800 hover:border-orange-300 hover:bg-orange-50"
                    }`}
                  >
                    {s.viewPlan}
                  </Link>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 md:py-24" data-testid="faq-section">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
          <Reveal className="text-center">
            <Eyebrow>{s.faqEyebrow}</Eyebrow>
            <h2 className="mt-4 font-display text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl lg:text-4xl">
              {s.faqTitle}
            </h2>
          </Reveal>
          <Reveal delay={0.1} className="mt-10">
            <Accordion type="single" collapsible className="w-full" data-testid="faq-accordion">
              {s.faqs.map((f, i) => (
                <AccordionItem key={i} value={`faq-${i}`} data-testid={`faq-item-${i}`}>
                  <AccordionTrigger data-testid={`faq-question-${i}`} className="text-left font-display text-base font-semibold text-slate-900">
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-sm leading-relaxed text-slate-600">
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Reveal>
        </div>
      </section>

      {/* FINAL CTA */}
      <section id="contact" className="mx-auto max-w-7xl px-4 pb-20 sm:px-6 lg:px-8" data-testid="final-cta-section">
        <Reveal>
          <div className="relative overflow-hidden rounded-3xl bg-orange-600 px-6 py-14 text-center md:py-20">
            <div className="pointer-events-none absolute inset-0" aria-hidden>
              <div className="absolute -top-20 left-1/4 h-64 w-64 rounded-full bg-white/10 blur-3xl" />
              <div className="absolute -bottom-20 right-1/4 h-64 w-64 rounded-full bg-amber-300/20 blur-3xl" />
            </div>
            <div className="relative">
              <h2 className="mx-auto max-w-2xl font-display text-2xl font-bold tracking-tight text-white sm:text-3xl lg:text-4xl">
                {s.ctaTitle}
              </h2>
              <p className="mx-auto mt-4 max-w-xl text-base text-orange-50">{s.ctaDesc}</p>
              <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
                <Link
                  to="/onboarding"
                  data-testid="final-cta-start-setup-button"
                  className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-white px-8 py-3.5 text-base font-semibold text-orange-700 transition-colors hover:bg-orange-50 sm:w-auto"
                >
                  {NAV_LABELS_CTA(lang)} <ArrowRight className="h-4 w-4" />
                </Link>
                <button
                  data-testid="final-cta-book-demo-button"
                  onClick={() => setDemoOpen(true)}
                  className="inline-flex w-full items-center justify-center gap-2 rounded-xl border-2 border-white/40 px-8 py-3.5 text-base font-semibold text-white transition-colors hover:bg-white/10 sm:w-auto"
                >
                  {s.ctaDemo}
                </button>
              </div>
            </div>
          </div>
        </Reveal>
      </section>

      <Dialog open={demoOpen} onOpenChange={setDemoOpen}>
        <DialogContent data-testid="book-demo-dialog" className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display">Book a free demo</DialogTitle>
            <DialogDescription>
              Tell us where to reach you and we will call you back within one working day.
            </DialogDescription>
          </DialogHeader>
          <form
            className="space-y-4"
            onSubmit={(e) => {
              e.preventDefault();
              setDemoOpen(false);
              toast.success("Design preview: demo request submitted");
            }}
          >
            <input data-testid="demo-name-input" required placeholder="Your name" className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-200" />
            <input data-testid="demo-phone-input" required placeholder="Mobile number" className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-200" />
            <button data-testid="demo-submit-button" className="w-full rounded-xl bg-orange-600 px-5 py-3 text-sm font-semibold text-white hover:bg-orange-700">
              Request a call back
            </button>
          </form>
        </DialogContent>
      </Dialog>

      <SiteFooter />
    </div>
  );
}

function NAV_LABELS_CTA(lang) {
  return lang === "hi" ? "सेटअप शुरू करें" : "Start Setup";
}
